'use client';

import { motion } from 'framer-motion';
import { useRouter } from 'next/navigation';

interface AlreadyPairedModalProps {
    isOpen: boolean;
    deviceId: string;
    onBackToStart?: () => void;
    onPairNewDevice?: () => void;
}

export function AlreadyPairedModal({
    isOpen,
    deviceId,
    onBackToStart,
    onPairNewDevice,
}: AlreadyPairedModalProps) {
    const router = useRouter();

    const handleBackToStart = () => {
        if (onBackToStart) {
            onBackToStart();
        } else {
            // Fallback to router navigation
            router.push('/');
        }
    };

    const handlePairNewDevice = () => {
        if (onPairNewDevice) {
            onPairNewDevice();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
            <motion.div
                className="bg-gray-900 rounded-2xl max-w-md w-full p-8 text-center relative border border-gray-700"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
            >
                {/* Icon */}
                <div className="mb-6">
                    <div className="w-20 h-20 bg-yellow-900/20 border border-yellow-700/50 rounded-full mx-auto flex items-center justify-center">
                        <svg
                            className="w-10 h-10 text-yellow-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                    </div>
                </div>

                {/* Title */}
                <h2 className="text-2xl font-bold text-white mb-4">
                    Device Already Paired
                </h2>

                {/* Description */}
                <p className="text-gray-400 mb-6">
                    This device (ID:{' '}
                    <span className="font-mono text-sm text-yellow-500">
                        {deviceId}
                    </span>
                    ) is already paired with another account.
                </p>

                {/* Options */}
                <div className="bg-gray-800/50 rounded-lg p-5 mb-6 text-left border border-gray-700">
                    <p className="text-gray-300 mb-4">You have two options:</p>

                    <div className="space-y-4">
                        <div className="flex items-start">
                            <div className="flex-shrink-0 w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center mt-0.5">
                                <span className="text-purple-400 text-xs font-bold">
                                    1
                                </span>
                            </div>
                            <div className="ml-3">
                                <p className="text-white font-medium">
                                    Unpair and re-pair this device
                                </p>
                                <p className="text-gray-500 text-sm mt-1">
                                    Sign in to the original account, unpair the
                                    device, then come back here
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start">
                            <div className="flex-shrink-0 w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center mt-0.5">
                                <span className="text-purple-400 text-xs font-bold">
                                    2
                                </span>
                            </div>
                            <div className="ml-3">
                                <p className="text-white font-medium">
                                    Pair a different device
                                </p>
                                <p className="text-gray-500 text-sm mt-1">
                                    Scan the QR code on another screen
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA Buttons */}
                <div className="space-y-3">
                    <button
                        onClick={handlePairNewDevice}
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 transform hover:scale-105"
                    >
                        Pair Another Device
                    </button>

                    <button
                        onClick={handleBackToStart}
                        className="w-full text-gray-400 hover:text-white transition-colors duration-200"
                    >
                        Cancel
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
